import React, { useState, useMemo, useCallback } from "react";
import {
    Box,
    Typography,
    Paper,
    Button,
    IconButton,
    Tooltip,
    Chip,
    Snackbar,
    Alert,
    TextField,
    InputAdornment,
    Fab,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    ToggleButtonGroup,
    ToggleButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
    Edit as EditIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
} from "@mui/icons-material";

import ServicesTypeProjectForm from "./ServicesTypeProjectForm";
import { useServicesTypesProject } from "../../../hooks/administration/useServicesTypesProject";
import { useAuthContext } from "../../../context/AuthProvider";

const ServicesTypesProjectDashboard = () => {
    const [open, setOpen] = useState(false);
    const [editTypeService, setEditTypeService] = useState(null);
    const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");

    const [openConfirm, setOpenConfirm] = useState(false);
    const [selectedTypeService, setSelectedTypeService] = useState(null);

    const { user } = useAuthContext();
    const {
        servicesTypesProject = [],
        isLoading,
        createMutation,
        updateMutation,
        deleteMutation,
    } = useServicesTypesProject();

    const showAlert = useCallback((message, severity = "success") => {
        setAlert({ open: true, message, severity });
    }, []);

    const handleOpenCreate = useCallback(() => {
        setEditTypeService(null);
        setOpen(true);
    }, []);

    const handleOpenEdit = useCallback((typeService) => {
        setEditTypeService(typeService);
        setOpen(true);
    }, []);

    const handleCloseForm = useCallback(() => {
        setOpen(false);
        setEditTypeService(null);
    }, []);

    const handleOpenConfirm = useCallback((typeService) => {
        setSelectedTypeService(typeService);
        setOpenConfirm(true);
    }, []);

    const handleCloseConfirm = useCallback(() => {
        setOpenConfirm(false);
        setSelectedTypeService(null);
    }, []);

    const handleSubmitRoute = useCallback(
        (data) => {
            if (!user) {
                showAlert("Debe iniciar sesión para realizar esta acción", "error");
                return;
            }

            if (editTypeService) {
                updateMutation.mutate(
                    {
                        id: editTypeService.serproid,
                        data: { ...data, updbyid: user.EmpID },
                    },
                    {
                        onSuccess: () => {
                            showAlert("Servicio de proyecto actualizado correctamente");
                            handleCloseForm();
                        },
                        onError: (err) =>
                            showAlert(err?.message || "Error al actualizar el servicio de proyecto", "error"),
                    }
                );
            } else {
                createMutation.mutate(
                    { ...data, crebyid: user.EmpID },
                    {
                        onSuccess: () => {
                            showAlert("Servicio de proyecto creado con éxito");
                            handleCloseForm();
                        },
                        onError: (err) =>
                            showAlert(err?.message || "Error al crear el servicio de proyecto", "error"),
                    }
                );
            }
        },
        [user, editTypeService, updateMutation, createMutation, showAlert, handleCloseForm]
    );

    const handleConfirmDelete = useCallback(() => {
        if (!selectedTypeService) return;

        deleteMutation.mutate(selectedTypeService.serproid, {
            onSuccess: () => {
                showAlert("Servicio de proyecto eliminado con éxito");
                handleCloseConfirm();
            },
            onError: (err) => {
                showAlert(err?.message || "Error al eliminar el servicio de proyecto", "error");
                handleCloseConfirm();
            },
        });
    }, [selectedTypeService, deleteMutation, showAlert, handleCloseConfirm]);

    const normalizedSearch = search.trim().toLowerCase();

    const rows = useMemo(() => {
        let data = servicesTypesProject;

        if (statusFilter === "A") {
            data = data.filter((s) => s.estreg === "A");
        } else if (statusFilter === "NOT_A") {
            data = data.filter((s) => s.estreg !== "A");
        }

        if (normalizedSearch) {
            data = data.filter((s) => {
                const servicioProyecto = (s.serpronom || "").toLowerCase();
                const descripcion = (s.serprodes || "").toLowerCase();
                const proyecto = (s.pronom || "").toLowerCase();
                const tipoServicio = (s.tipsernom || "").toLowerCase();

                return (
                    servicioProyecto.includes(normalizedSearch) ||
                    descripcion.includes(normalizedSearch) ||
                    proyecto.includes(normalizedSearch) ||
                    tipoServicio.includes(normalizedSearch)
                );
            });
        }

        return data;
    }, [servicesTypesProject, statusFilter, normalizedSearch]);

    const columns = useMemo(
        () => [
            {
                field: "serpronom",
                headerName: "Servicio Proyecto",
                flex: 1,
                minWidth: 220,
                valueGetter: (params) => params.row?.serpronom || "—",
            },
            {
                field: "tipsernom",
                headerName: "Tipo Servicio",
                flex: 1,
                minWidth: 180,
                valueGetter: (params) => params.row?.tipsernom || "—",
            },
            {
                field: "pronom",
                headerName: "Proyecto",
                flex: 1,
                minWidth: 160,
                valueGetter: (params) => params.row?.pronom || "—",
            },
            {
                field: "serpropre",
                headerName: "Precio",
                width: 120,
                valueGetter: (params) => Number(params.row?.serpropre || 0).toFixed(2),
            },
            {
                field: "serprouni",
                headerName: "Unidad",
                width: 110,
                valueGetter: (params) => params.row?.serprouni || "—",
            },
            {
                field: "serproper",
                headerName: "Permanente",
                width: 130,
                renderCell: (params) => (
                    <Chip
                        label={params.value ? "Sí" : "No"}
                        color={params.value ? "success" : "default"}
                        size="small"
                        variant="outlined"
                    />
                ),
            },
            {
                field: "serproint",
                headerName: "Interno",
                width: 110,
                renderCell: (params) => (
                    <Chip
                        label={params.value ? "Sí" : "No"}
                        color={params.value ? "info" : "default"}
                        size="small"
                        variant="outlined"
                    />
                ),
            },
            {
                field: "estreg",
                headerName: "Estado",
                width: 120,
                renderCell: (params) => {
                    const isActive = params.value === "A";
                    return (
                        <Chip
                            label={isActive ? "ACTIVO" : "INACTIVO"}
                            color={isActive ? "success" : "warning"}
                            size="small"
                            variant="outlined"
                        />
                    );
                },
            },
            {
                field: "actions",
                headerName: "Acciones",
                width: 120,
                sortable: false,
                filterable: false,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => handleOpenEdit(params.row)}>
                                <EditIcon color="primary" fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Eliminar">
                            <IconButton
                                size="small"
                                onClick={() => handleOpenConfirm(params.row)}
                                disabled={deleteMutation.isPending}
                            >
                                <DeleteIcon color="error" fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                ),
            },
        ],
        [handleOpenEdit, handleOpenConfirm, deleteMutation.isPending]
    );

    return (
        <Paper
            sx={{
                p: 2.5,
                borderRadius: 2,
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#f9f9f9",
            }}
        >
            <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                    <Typography variant="h5" fontWeight={700}>
                        Servicios por Proyecto
                    </Typography>

                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={statusFilter}
                        onChange={(_, val) => val && setStatusFilter(val)}
                    >
                        <ToggleButton value="ALL">Todos</ToggleButton>
                        <ToggleButton value="A">Activos</ToggleButton>
                        <ToggleButton value="NOT_A">Inactivos</ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <TextField
                    size="small"
                    label="Buscar por servicio, tipo, descripción o proyecto..."
                    fullWidth
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <Tooltip title="Limpiar">
                                    <IconButton size="small" onClick={() => setSearch("")}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        ) : null,
                    }}
                />

                <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        loading={isLoading}
                        getRowId={(row) => row.serproid}
                        disableRowSelectionOnClick
                        pageSizeOptions={[15, 20, 25, 50, 100]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 20, page: 0 } },
                            sorting: { sortModel: [{ field: "serpronom", sort: "asc" }] },
                        }}
                        sx={{
                            borderRadius: 2,
                            backgroundColor: "#fff",
                            "& .MuiDataGrid-columnHeaders": {
                                fontWeight: 700,
                                backgroundColor: "#f0f0f0",
                            },
                        }}
                    />
                </Box>
            </Stack>

            <Fab
                color="primary"
                aria-label="add"
                sx={{ position: "fixed", bottom: 20, right: 20 }}
                onClick={handleOpenCreate}
            >
                <AddIcon />
            </Fab>

            {open && (
                <ServicesTypeProjectForm
                    open={open}
                    handleClose={handleCloseForm}
                    onSubmit={handleSubmitRoute}
                    defaultValues={editTypeService || {}}
                />
            )}

            <Dialog open={openConfirm} onClose={handleCloseConfirm}>
                <DialogTitle>Confirmar eliminación</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro de que desea eliminar el servicio de proyecto{" "}
                        <strong>{selectedTypeService?.serpronom}</strong>?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirm} color="inherit">
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        disabled={deleteMutation.isPending}
                    >
                        {deleteMutation.isPending ? "Eliminando..." : "Eliminar"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={alert.open}
                autoHideDuration={4000}
                onClose={() => setAlert((a) => ({ ...a, open: false }))}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert
                    onClose={() => setAlert((a) => ({ ...a, open: false }))}
                    severity={alert.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {alert.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default ServicesTypesProjectDashboard;